import React, { useEffect, useRef, useState } from 'react';

/**
 * CountUp — smoothly animates a number from 0 to `end` on mount.
 * Used for the hero social-proof strip (review count, average rating).
 *
 * Implementation notes:
 *  - The effect must be Strict-Mode safe. React 18 invokes effects twice on
 *    mount (mount → cleanup → mount). The cleanup cancels the active rAF; we
 *    must then re-start fresh on the second mount, so we do NOT guard with a
 *    persistent ref. The cleanup is the source of truth.
 */
const CountUp = ({ end = 0, duration = 1500, decimals = 0, prefix = '', suffix = '', className = '', ...rest }) => {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const target = Number(end) || 0;
    if (target === 0) {
      setValue(0);
      return undefined;
    }
    const start = performance.now();
    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic for a calmer settle at the end
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setValue(target);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [end, duration]);

  return (
    <span className={className} {...rest}>
      {prefix}{value.toFixed(decimals)}{suffix}
    </span>
  );
};

export default CountUp;
